import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { AlertTriangle, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateExpirationMinutes } from '@/hooks/admin/organization/use-update-expiration-minutes';
import {
  formatExpirationMinutes,
  ONE_MONTH_MINUTES,
} from './default-expiration-utils';

type DefaultExpirationModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  currentExpirationMinutes: number;
};

const MINUTES_PER_DAY = 24 * 60;

function sanitizeNumericInput(value: string): string {
  return value.replace(/\D/g, '');
}

function getDaysField(totalMinutes: number): string {
  return String(Math.max(1, Math.ceil(totalMinutes / MINUTES_PER_DAY)));
}

export function DefaultExpirationModal({
  open,
  onClose,
  onSaved,
  currentExpirationMinutes,
}: DefaultExpirationModalProps) {
  const [days, setDays] = useState<string>(() =>
    getDaysField(currentExpirationMinutes)
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isConfirmingLongDuration, setIsConfirmingLongDuration] =
    useState(false);

  const { updateExpirationMinutes, isLoading, error } =
    useUpdateExpirationMinutes();

  useEffect(() => {
    // Case: modal reopened - reset local draft state from the current persisted value.
    if (!open) {
      return;
    }

    setDays(getDaysField(currentExpirationMinutes));
    setValidationError(null);
    setIsConfirmingLongDuration(false);
  }, [open, currentExpirationMinutes]);

  const parsedDays = useMemo(() => (days === '' ? 0 : Number(days)), [days]);
  const totalMinutes = parsedDays * MINUTES_PER_DAY;

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setValidationError(null);
    setIsConfirmingLongDuration(false);
    onClose();
  };

  const handleDaysChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDays(sanitizeNumericInput(event.target.value));
    setValidationError(null);
    setIsConfirmingLongDuration(false);
  };

  const validateTotalMinutes = (): number | null => {
    if (totalMinutes <= 0) {
      setValidationError('Expiration must be at least 1 day.');
      return null;
    }

    setValidationError(null);
    return totalMinutes;
  };

  const saveExpiration = async (minutes: number) => {
    await updateExpirationMinutes({ expirationMinutes: minutes });
    await onSaved();
    onClose();
  };

  const handleSave = async () => {
    const validatedTotalMinutes = validateTotalMinutes();
    if (validatedTotalMinutes == null) {
      return;
    }

    // Case: long durations require an explicit confirmation step.
    if (validatedTotalMinutes > ONE_MONTH_MINUTES) {
      setIsConfirmingLongDuration(true);
      return;
    }

    await saveExpiration(validatedTotalMinutes);
  };

  const handleConfirmLongDuration = async () => {
    const validatedTotalMinutes = validateTotalMinutes();
    if (validatedTotalMinutes == null) {
      // Case: invalid input after returning from confirmation - fall back to edit mode.
      setIsConfirmingLongDuration(false);
      return;
    }

    await saveExpiration(validatedTotalMinutes);
  };

  if (isConfirmingLongDuration) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm long expiration time</DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                This expiration time is longer than one month. Are you sure you
                want new proposals and account transactions to stay open for
                this long?
              </p>
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <div className="space-y-1 text-sm">
                  <div>
                    Current setting:{' '}
                    <strong>
                      {formatExpirationMinutes(currentExpirationMinutes)}
                    </strong>
                  </div>
                  <div>
                    New setting:{' '}
                    <strong>{formatExpirationMinutes(totalMinutes)}</strong>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmingLongDuration(false)}
              disabled={isLoading}
            >
              Go Back
            </Button>
            <Button onClick={handleConfirmLongDuration} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Anyway'}
            </Button>
          </DialogFooter>
          {error && (
            <p className="mt-2 text-sm text-red-600">
              {error.message || 'Failed to update expiration'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update expiration time</DialogTitle>
          <DialogDescription className="space-y-3">
            <p>
              Set the number of days before new proposals and account
              transactions expire. This will not affect any existing proposals
              or transactions.
            </p>
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <div>
                  Current setting:{' '}
                  <strong>
                    {formatExpirationMinutes(currentExpirationMinutes)}
                  </strong>
                </div>
                {totalMinutes > 0 ? (
                  <div>
                    New setting:{' '}
                    <strong>{formatExpirationMinutes(totalMinutes)}</strong>
                  </div>
                ) : null}
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="max-w-xs space-y-2">
          <label className="text-sm font-medium" htmlFor="expiration-days">
            Days
          </label>
          <Input
            id="expiration-days"
            inputMode="numeric"
            // Case: the UI is days-only, so we keep the field numeric and convert to minutes internally.
            value={days}
            onChange={handleDaysChange}
            placeholder="0"
          />
        </div>

        {validationError ? (
          <p className="text-sm text-red-600">{validationError}</p>
        ) : null}
        {error ? (
          <p className="text-sm text-red-600">
            {error.message || 'Failed to update expiration'}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
